/**
 * Chatbot assistant virtuel Otulaa
 *
 * Rôle : Fenêtre de chat flottante ; envoie les messages à /api/chatbot, affiche réponses et suggestions.
 * Utilise la page courante et la session pour les suggestions contextuelles (/api/chatbot/suggestions).
 * Technologies : React, next-auth (useSession), Next.js (usePathname), API chatbot + chatbot/suggestions
 */
"use client";

import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Search, Lightbulb } from "lucide-react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  intent?: string;
  confidence?: number;
}

interface QuickAction {
  id: string;
  label: string;
  description: string;
  href: string;
  category: string;
  priority: number;
}

interface Suggestion {
  id: string;
  type: string;
  title: string;
  description: string;
  data?: Record<string, unknown>;
  priority: number;
  category: string;
}

export default function EnhancedChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Bonjour ! Je suis l'assistant virtuel d'Otulaa. Comment puis-je vous aider aujourd'hui ? 😊",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();
  const { data: session } = useSession();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      loadSuggestions();
      loadQuickActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadSuggestions = async () => {
    try {
      const response = await fetch(`/api/chatbot/suggestions?userId=${session?.user?.id || 'anonymous'}`);
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des suggestions:', error);
    }
  };

  const loadQuickActions = async () => {
    try {
      const response = await fetch(`/api/chatbot/suggestions?type=quick_actions&userId=${session?.user?.id || 'anonymous'}`);
      const data = await response.json();
      if (data.success) {
        setQuickActions(data.suggestions);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des actions rapides:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setShowSuggestions(false);

    try {
      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputValue,
          userId: session?.user?.id || 'anonymous',
          currentPage: pathname
        }),
      });

      const data = await response.json();

      if (data.success) {
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: data.response,
          isUser: false,
          timestamp: new Date(),
          intent: data.intent,
          confidence: data.confidence
        };

        setMessages(prev => [...prev, botMessage]);

        // Mise à jour des suggestions si disponibles
        if (data.suggestions && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
        }
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "Désolé, je rencontre un problème technique. Veuillez réessayer.",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (action: QuickAction) => {
    if (action.href.startsWith('http') || action.href.startsWith('mailto:')) {
      window.open(action.href, '_blank');
    } else {
      window.location.href = action.href;
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setInputValue(suggestion.title);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/chatbot/search?q=${encodeURIComponent(query)}&userId=${session?.user?.id || 'anonymous'}`);
      const data = await response.json();

      if (data.success && data.results.length > 0) {
        let searchResponse = `Voici ce que j'ai trouvé pour "${query}":\n\n`;
        data.results.slice(0, 5).forEach((result: { title: string; snippet: string; href: string }, index: number) => {
          searchResponse += `${index + 1}. **${result.title}**\n`;
          searchResponse += `${result.snippet}\n`;
          searchResponse += `→ ${result.href}\n\n`;
        });

        const searchMessage: Message = {
          id: Date.now().toString(),
          text: searchResponse,
          isUser: false,
          timestamp: new Date(),
          intent: 'search_request'
        };

        setMessages(prev => [...prev, searchMessage]);
      } else {
        const noResultsMessage: Message = {
          id: Date.now().toString(),
          text: `Je n'ai pas trouvé de résultats pour "${query}". Pouvez-vous être plus spécifique ?`,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, noResultsMessage]);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Bouton flottant avec bulle d'aide */}
      <div 
        className="fixed bottom-6 left-6 z-50"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
        >
          {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        </button>

        {/* Bulle d'aide au survol */}
        {isHovered && !isOpen && (
          <div className="absolute bottom-16 left-0">
            <div className="absolute bottom-0 left-6 w-0 h-0 border-l-4 border-r-4 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-64 relative">
              <p className="text-sm text-gray-700 leading-relaxed">
                Je suis l&apos;assistant virtuel d&apos;Otulaa. Comment puis-je vous aider ?
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Fenêtre du chatbot améliorée */}
      {isOpen && (
        <div className="fixed bottom-24 left-6 z-40 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white p-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle size={16} />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Assistant Otulaa</h3>
                  <p className="text-xs opacity-90">En ligne</p>
                </div>
              </div>
              <button
                onClick={() => setShowSuggestions(!showSuggestions)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Suggestions"
              >
                <Lightbulb size={16} />
              </button>
            </div>
          </div>

          {/* Suggestions rapides */}
          {showSuggestions && (
            <div className="p-3 bg-gray-50 border-b border-gray-200 max-h-32 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {quickActions.slice(0, 4).map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action)}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-full text-xs hover:bg-gray-100 transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                    message.isUser
                      ? "bg-gradient-to-r from-orange-400 to-red-500 text-white"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  <p className="whitespace-pre-line">{message.text}</p>
                  <div className={`text-xs mt-1 flex items-center gap-2 ${
                    message.isUser ? "text-white/70" : "text-gray-500"
                  }`}>
                    <span>
                      {message.timestamp.toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                    {message.confidence && (
                      <span className="text-xs opacity-75">
                        ({Math.round(message.confidence * 100)}%)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 p-3 rounded-2xl text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions de recherche */}
          {suggestions.length > 0 && !showSuggestions && (
            <div className="p-3 bg-gray-50 border-t border-gray-200 max-h-24 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {suggestions.slice(0, 3).map((suggestion) => (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-full text-xs hover:bg-gray-100 transition-colors flex items-center gap-1"
                  >
                    <Search size={12} />
                    {suggestion.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tapez votre message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
            
            {/* Actions rapides */}
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => handleSearch('fournisseurs')}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <Search size={12} />
                Fournisseurs
              </button>
              <button
                onClick={() => handleSearch('stocks')}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <Search size={12} />
                Stocks
              </button>
              <button
                onClick={() => handleSearch('aide')}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <Search size={12} />
                Aide
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
